
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useDealsData = (searchTerm: string, countryFilter: string, priceFilter: string) => {
  const [vacations, setVacations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState<string[]>([]);

  const fetchCountries = async () => {
    try {
      const { data, error } = await supabase
        .from('TUI')
        .select('country')
        .not('country', 'is', null)
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .not('price', 'is', null);
      
      if (error) throw error;
      
      const uniqueCountries = [...new Set(data.map(item => item.country))].sort();
      setCountries(uniqueCountries);
    } catch (error: any) {
      console.error('Error fetching countries:', error);
    }
  };

  const buildQuery = (baseQuery: any) => {
    let query = baseQuery;

    if (searchTerm) {
      query = query.or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`);
    }

    if (countryFilter !== "all") {
      query = query.eq('country', countryFilter);
    }

    // Apply price filters
    if (priceFilter === '500') {
      query = query.lt('price', 500);
    } else if (priceFilter === '1000') {
      query = query.gte('price', 500).lt('price', 1000);
    } else if (priceFilter === '2000') {
      query = query.gte('price', 1000).lt('price', 2000);
    } else if (priceFilter === '3500') {
      query = query.gte('price', 2000).lt('price', 3500);
    } else if (priceFilter === '5000') {
      query = query.gte('price', 3500).lt('price', 5000);
    } else if (priceFilter === '5000+') {
      query = query.gte('price', 5000);
    }

    return query;
  };

  const fetchSmartDeals = async () => {
    setLoading(true);
    try {
      console.log('Fetching smart deals based on user behavior...');
      
      // Get popular destinations from search performance
      const { data: searchPerformance } = await supabase
        .from('search_performance')
        .select('search_query, click_through_rate')
        .gte('click_through_rate', 0.15)
        .gte('total_searches', 2)
        .order('click_through_rate', { ascending: false })
        .limit(10);

      // Extract countries from popular searches
      const popularCountries = new Set<string>();
      const countryKeywords = ['spanje', 'turkije', 'griekenland', 'italië', 'frankrijk', 'portugal', 'kroatië', 'duitsland'];
      
      searchPerformance?.forEach(search => {
        const query = search.search_query.toLowerCase();
        countryKeywords.forEach(keyword => {
          if (query.includes(keyword)) {
            popularCountries.add(keyword.charAt(0).toUpperCase() + keyword.slice(1));
          }
        });
      });

      let baseQuery = supabase
        .from('TUI')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .not('price', 'is', null)
        .order('price', { ascending: true });

      let query = buildQuery(baseQuery);

      // Prioritize popular countries if no specific filter is set
      if (countryFilter === "all" && popularCountries.size > 0) {
        query = query.in('country', Array.from(popularCountries));
        console.log('Using popular countries preference:', Array.from(popularCountries));
      }

      const { data, error } = await query.limit(50);
      
      if (error) throw error;
      
      // If smart mode didn't return enough results, fall back to regular search
      if (popularCountries.size > 0 && (!data || data.length < 10)) {
        console.log('Smart mode returned few results, falling back to regular search');
        await fetchRegularDeals();
        return;
      }
      
      setVacations(data || []);
      console.log(`Smart deals loaded: ${data?.length || 0} results`);
      
    } catch (error: any) {
      toast.error("Fout bij het laden van deals");
      console.error('Error fetching smart deals:', error);
      await fetchRegularDeals();
    } finally {
      setLoading(false);
    }
  };

  const fetchRegularDeals = async () => {
    try {
      let baseQuery = supabase
        .from('TUI')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .not('price', 'is', null)
        .order('price', { ascending: true });

      const query = buildQuery(baseQuery);
      const { data, error } = await query.limit(50);
      
      if (error) throw error;
      
      setVacations(data || []);
    } catch (error: any) {
      toast.error("Fout bij het laden van deals");
      console.error('Error fetching regular deals:', error);
    }
  };

  const fetchVacations = async () => {
    await fetchSmartDeals();
  };

  useEffect(() => {
    fetchVacations();
    fetchCountries();
  }, []);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      fetchVacations();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, countryFilter, priceFilter]);

  return {
    vacations,
    loading,
    countries
  };
};
